import { useState, useMemo, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import api, { fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Camera, X } from "lucide-react";
import { logSwallow } from "../lib/log";
import i18n, { SUPPORTED_CODES } from "../i18n";
import LanguageSelector from "../components/app/LanguageSelector";

// Best-effort device language → one of our launch codes (else null).
function detectDeviceLang() {
  const cands = [navigator.language, ...(navigator.languages || [])];
  for (const c of cands) {
    const base = (c || "").toLowerCase().split("-")[0];
    if (SUPPORTED_CODES.includes(base)) return base;
  }
  return null;
}

export function LoginPage() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await login(email, password);
      toast.success(t("auth.welcomeBack", { name: u.display_name || u.real_name || "explorer" }));
      nav(u.role === "admin" ? "/admin" : "/home");
    } catch (e) {
      setErr(fmtErr(e));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title={t("auth.signInTitle")}>
      <form onSubmit={submit} className="space-y-3" data-testid="login-form">
        <input data-testid="login-email" required type="email" placeholder={t("auth.email")} value={email} onChange={e=>setEmail(e.target.value)} className="auth-input" />
        <input data-testid="login-password" required type="password" placeholder={t("auth.password")} value={password} onChange={e=>setPassword(e.target.value)} className="auth-input" />
        {err && <p className="text-[#FF1A1A] text-sm" data-testid="login-error">{err}</p>}
        <button data-testid="login-submit" disabled={busy} className="auth-btn">{busy ? t("common.loading") : t("auth.signInTitle")}</button>
      </form>
      <p className="text-zinc-500 text-sm mt-6 text-center">{t("auth.noAccount")} <Link to="/register" className="text-white" data-testid="goto-register">{t("auth.signUpLink")}</Link></p>
    </AuthShell>
  );
}

// Client-side helpers mirroring backend rules — kept simple; backend is
// source of truth. These only give live inline feedback.
function computeAge(dobStr) {
  if (!dobStr) return null;
  const d = new Date(dobStr);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age;
}
function normalize(s) { return (s || "").toLowerCase().replace(/[^a-z0-9]/g, ""); }
function namesTooSimilar(ghost, real) {
  const g = normalize(ghost), r = normalize(real);
  if (!g || !r) return false;
  if (g === r) return true;
  const [short, long_] = g.length < r.length ? [g, r] : [r, g];
  if (short.length >= 3 && long_.includes(short)) return true;
  for (const tok of (real || "").split(/[^A-Za-z0-9]+/)) {
    const t = tok.toLowerCase();
    if (t.length >= 3 && g.includes(t)) return true;
  }
  return false;
}

export function RegisterPage() {
  const { register } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const suggestedLang = useMemo(() => detectDeviceLang(), []);
  const [form, setForm] = useState({
    email: "", password: "",
    real_name: "", display_name: "",
    dob: "", hometown: "",
    gender_icon: "neutral", mode_default: "anonymous", city: "NYC",
    preferred_language: suggestedLang || i18n.resolvedLanguage || "en",
    current_occupation: "", career_interests: [],
  });

  // Apply the device-suggested language immediately so onboarding starts in it.
  useEffect(() => {
    const initial = suggestedLang || i18n.resolvedLanguage || "en";
    if (i18n.resolvedLanguage !== initial) i18n.changeLanguage(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickLanguage = (code) => {
    setForm((f) => ({ ...f, preferred_language: code }));
    i18n.changeLanguage(code); // rest of onboarding + UI switches instantly
  };
  // Avatar is picked client-side BEFORE the account exists (backend requires
  // auth to accept the upload). We buffer the File in memory + show a preview,
  // then POST /api/avatar right after register() succeeds — one flow.
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);   // object URL for <img>
  const avatarInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Free the object URL when the file changes or on unmount
  useEffect(() => {
    if (!avatarFile) { setAvatarPreview(null); return undefined; }
    const url = URL.createObjectURL(avatarFile);
    setAvatarPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  const age = useMemo(() => computeAge(form.dob), [form.dob]);
  const underAge = age !== null && age < 18;
  const ghostLeak = useMemo(
    () => namesTooSimilar(form.display_name, form.real_name),
    [form.display_name, form.real_name]
  );

  const cards = [
    { kicker: t("onboarding.card0Kicker"), title: t("onboarding.card0Title"), body: t("onboarding.card0Body") },
    { kicker: t("onboarding.card1Kicker"), title: t("onboarding.card1Title"), body: t("onboarding.card1Body") },
    { kicker: t("onboarding.card2Kicker"), title: t("onboarding.card2Title"), body: t("onboarding.card2Body") },
  ];

  const pickAvatar = () => avatarInputRef.current?.click();
  const clearAvatar = () => {
    setAvatarFile(null);
    if (avatarInputRef.current) avatarInputRef.current.value = "";
  };
  const onPickAvatar = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Only JPEG, PNG, or WebP images");
      e.target.value = "";
      return;
    }
    if (f.size > 3 * 1024 * 1024) {
      toast.error("Image must be under 3MB");
      e.target.value = "";
      return;
    }
    setAvatarFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (underAge) { setErr(t("auth.mustBe18")); return; }
    if (ghostLeak) { setErr(t("auth.aliasTooSimilar")); return; }
    setBusy(true); setErr("");
    try {
      const u = await register(form);
      toast.success(t("auth.welcomeBack", { name: u.display_name }));
      // Upload the buffered avatar (if any) now that we're authenticated.
      // Failure here is non-fatal — the user still gets into the app and can
      // retry from Settings. We only surface a toast, don't block navigation.
      if (avatarFile) {
        try {
          const fd = new FormData();
          fd.append("file", avatarFile, avatarFile.name || "avatar");
          await api.post("/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (uploadErr) {
          logSwallow("register.avatar.upload", uploadErr);
          toast.error("Signed up, but photo upload failed. Try again in Settings.");
        }
      }
      nav("/home");
    } catch (e) {
      setErr(fmtErr(e));
    } finally { setBusy(false); }
  };

  if (step === 0) {
    return (
      <AuthShell title={t("language.kicker")}>
        <div className="space-y-5" data-testid="onb-language-step">
          <p className="font-mono-accent text-[#E60000]">{t("language.kicker")}</p>
          <h2 className="font-display text-3xl tracking-tighter">{t("language.title")}</h2>
          <p className="text-zinc-400 leading-relaxed">{t("language.subtitle")}</p>
          <LanguageSelector value={form.preferred_language} onChange={pickLanguage} suggested={suggestedLang} />
          <button data-testid="onb-lang-next" onClick={()=>setStep(1)} className="auth-btn">{t("onboarding.next")}</button>
        </div>
      </AuthShell>
    );
  }

  if (step < 4) {
    const c = cards[step - 1];
    return (
      <AuthShell title={t("auth.welcome")}>
        <div className="space-y-6" data-testid={`onb-card-${step - 1}`}>
          <p className="font-mono-accent text-[#E60000]">{c.kicker}</p>
          <h2 className="font-display text-4xl tracking-tighter">{c.title}</h2>
          <p className="text-zinc-400 leading-relaxed">{c.body}</p>
          <div className="flex gap-2 pt-4">
            {[1,2,3].map(i=>(<div key={i} className={`h-1 flex-1 rounded-full ${i<=step?"bg-[#E60000]":"bg-white/10"}`} />))}
          </div>
          <button data-testid="onb-next" onClick={()=>setStep(step+1)} className="auth-btn">{step===3?t("onboarding.createAccount"):t("onboarding.next")}</button>
        </div>
      </AuthShell>
    );
  }

  const modeAvatarHint = form.mode_default === "real"
    ? "Visible mode — this photo will be public in Nearby Scan."
    : "Ghost mode — your photo stays hidden until you reveal it to a match.";

  return (
    <AuthShell title={t("auth.createAccount")}>
      <form onSubmit={submit} className="space-y-3" data-testid="register-form">
        {/* Optional profile picture — picked here, uploaded right after signup */}
        <div className="pb-3 border-b border-white/5" data-testid="reg-avatar-block">
          <label className="block text-xs font-mono-accent text-zinc-500 mb-2 tracking-wider">
            {t("fields.profilePictureOptional")}
          </label>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={pickAvatar}
              data-testid="reg-avatar-picker"
              className="relative w-20 h-20 rounded-full overflow-hidden border-2 border-white/10 bg-gradient-to-br from-[#1a0000] to-black flex items-center justify-center hover:border-[#E60000]/50 transition"
            >
              {avatarPreview ? (
                <img src={avatarPreview} alt="Your avatar preview" className="w-full h-full object-cover" data-testid="reg-avatar-preview" />
              ) : (
                <Camera size={26} className="text-zinc-500" />
              )}
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={pickAvatar}
                  data-testid="reg-avatar-upload-btn"
                  className="text-xs font-mono-accent tracking-wider text-white/90 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:border-white/30 transition"
                >
                  {avatarFile ? t("common.change").toUpperCase() : t("common.upload").toUpperCase()}
                </button>
                {avatarFile && (
                  <button
                    type="button"
                    onClick={clearAvatar}
                    data-testid="reg-avatar-clear-btn"
                    className="text-xs font-mono-accent tracking-wider text-zinc-500 hover:text-[#FF1A1A] px-3 py-1.5 rounded-full border border-white/10 hover:border-[#FF1A1A]/30 transition inline-flex items-center gap-1"
                  >
                    <X size={12} /> {t("common.remove").toUpperCase()}
                  </button>
                )}
              </div>
              <p className="text-[10px] text-zinc-600 mt-2 leading-relaxed">
                JPEG / PNG / WebP up to 3MB. Add later in Settings if you prefer.
              </p>
            </div>
          </div>
          <p className="text-[10px] text-zinc-600 mt-2 font-mono-accent tracking-wider">
            {modeAvatarHint}
          </p>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            data-testid="reg-avatar-file-input"
            onChange={onPickAvatar}
          />
        </div>

        <input data-testid="reg-email" required type="email" placeholder={t("auth.email")} value={form.email} onChange={e=>setForm({...form,email:e.target.value})} className="auth-input" />
        <input data-testid="reg-password" required type="password" placeholder={t("auth.passwordMin")} value={form.password} onChange={e=>setForm({...form,password:e.target.value})} className="auth-input" />
        <div>
          <label className="block text-xs font-mono-accent text-zinc-500 mb-1 tracking-wider">{t("fields.dobLabel")}</label>
          <input data-testid="reg-dob" required type="date" value={form.dob} onChange={e=>setForm({...form,dob:e.target.value})} className="auth-input" max={new Date().toISOString().slice(0,10)} />
          {underAge && <p data-testid="reg-underage" className="text-[#FF1A1A] text-xs mt-1">{t("fields.underage")}</p>}
          {age !== null && !underAge && <p className="text-zinc-500 text-xs mt-1">{t("fields.ageShownOnReveal", { age })}</p>}
        </div>
        <div>
          <label className="block text-xs font-mono-accent text-zinc-500 mb-1 tracking-wider">{t("fields.realNameLabel")}</label>
          <input data-testid="reg-real-name" required placeholder={t("fields.realNamePlaceholder")} value={form.real_name} onChange={e=>setForm({...form,real_name:e.target.value})} className="auth-input" />
        </div>
        <div>
          <label className="block text-xs font-mono-accent text-zinc-500 mb-1 tracking-wider">{t("fields.ghostAliasLabel")}</label>
          <input data-testid="reg-display-name" required placeholder={t("fields.ghostAliasPlaceholder")} value={form.display_name} onChange={e=>setForm({...form,display_name:e.target.value})} className="auth-input" />
          {ghostLeak && <p data-testid="reg-alias-leak" className="text-[#FF1A1A] text-xs mt-1">{t("fields.aliasLeak")}</p>}
        </div>
        <input data-testid="reg-hometown" placeholder={t("fields.hometownPlaceholder")} value={form.hometown} onChange={e=>setForm({...form,hometown:e.target.value})} className="auth-input" />
        <div className="grid grid-cols-3 gap-2">
          {["male","female","neutral"].map(g=>(
            <button type="button" key={g} data-testid={`reg-gender-${g}`}
              onClick={()=>setForm({...form,gender_icon:g})}
              className={`py-3 rounded-xl border text-sm capitalize ${form.gender_icon===g?"border-[#E60000] bg-[#E60000]/10":"border-white/10"}`}>{t(`fields.gender${g.charAt(0).toUpperCase()+g.slice(1)}`)}</button>
          ))}
        </div>
        <select data-testid="reg-city" value={form.city} onChange={e=>setForm({...form,city:e.target.value})} className="auth-input">
          {["NYC","LA","Miami","Miami Beach","Austin","Chicago","Other"].map(c=><option key={c}>{c}</option>)}
        </select>

        {/* Optional career personalization — powers OPPORTUNITY Mode ranking */}
        <div className="pt-3 border-t border-white/10">
          <label className="block text-xs font-mono-accent text-zinc-500 mb-1 tracking-wider">
            WHAT DO YOU DO FOR WORK? · OPTIONAL
          </label>
          <input
            data-testid="reg-current-occupation"
            placeholder="e.g. bartender, software engineer, nurse (optional)"
            maxLength={80}
            value={form.current_occupation}
            onChange={e=>setForm({...form,current_occupation:e.target.value})}
            className="auth-input"
          />
          <p className="text-zinc-500 text-xs mt-1">
            We use this only to rank OPPORTUNITY Mode results — never shown publicly, never asks about salary.
          </p>
        </div>
        <div>
          <label className="block text-xs font-mono-accent text-zinc-500 mb-1 tracking-wider">
            WHAT OPPORTUNITIES INTEREST YOU? · OPTIONAL · SELECT ANY
          </label>
          <div className="grid grid-cols-2 gap-2" data-testid="reg-career-interests">
            {[
              ["hospitality","Hospitality"],
              ["healthcare","Healthcare"],
              ["business_management","Business / Mgmt"],
              ["finance","Finance"],
              ["technology","Technology"],
              ["sales","Sales"],
              ["marketing","Marketing"],
              ["real_estate","Real Estate / Property"],
              ["legal","Legal"],
              ["skilled_trades","Skilled Trades"],
              ["retail","Retail"],
              ["food_beverage","Food & Beverage"],
              ["other","Other"],
            ].map(([val, label]) => {
              const on = form.career_interests.includes(val);
              return (
                <button
                  key={val}
                  type="button"
                  data-testid={`reg-interest-${val}`}
                  onClick={() => setForm({
                    ...form,
                    career_interests: on
                      ? form.career_interests.filter(x=>x!==val)
                      : [...form.career_interests, val],
                  })}
                  className={`py-2 px-3 rounded-xl border text-xs text-left transition ${
                    on ? "border-[#E60000] bg-[#E60000]/10 text-white"
                       : "border-white/10 text-zinc-300"
                  }`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {err && <p className="text-[#FF1A1A] text-sm" data-testid="reg-error">{err}</p>}
        <button data-testid="reg-submit" disabled={busy || underAge || ghostLeak} className="auth-btn">{busy?t("common.loading"):t("auth.getIn")}</button>
      </form>
      <p className="text-zinc-500 text-sm mt-6 text-center">{t("auth.haveAccount")} <Link to="/login" className="text-white" data-testid="goto-login">{t("auth.signInLink")}</Link></p>
    </AuthShell>
  );
}

function AuthShell({ title, children }) {
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-5 relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[420px] h-[420px] rounded-full bg-[#E60000]/20 blur-[120px]" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8" data-testid="back-home">
          <span className="w-2.5 h-2.5 rounded-full bg-[#E60000] shadow-[0_0_12px_rgba(230,0,0,0.8)]" />
          <span className="font-display text-lg tracking-tighter">BlinkLink</span>
        </Link>
        <h1 className="font-display text-4xl mb-8 tracking-tighter">{title}</h1>
        <div className="glass rounded-3xl p-6">{children}</div>
        <style>{`
          .auth-input { width:100%; background:rgba(0,0,0,0.5); border:1px solid rgba(255,255,255,0.1); border-radius: 12px; padding:14px 16px; color:white; }
          .auth-input:focus { outline:none; border-color:#E60000; }
          .auth-btn { width:100%; background:#E60000; color:white; font-weight:600; padding:14px; border-radius:9999px; transition:all 0.2s; }
          .auth-btn:hover { background:#FF1A1A; box-shadow:0 0 30px rgba(230,0,0,0.4); }
          .auth-btn:disabled { opacity:0.5; }
        `}</style>
      </div>
    </div>
  );
}
